import CalendarIcon from 'assets/images/icons/CalendarIcon.svg?react';
import { AccessTimeOutlined, ChevronLeft, ChevronRight, Clear } from 'components/icons';
import { Button } from 'components/ui/button';
import { Calendar, pickerOptionClassName } from 'components/ui/calendar';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from 'components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import {
    endOfDay,
    format as formatDate,
    isAfter,
    isBefore,
    isSameMonth,
    isValid,
    parse,
    setHours,
    setMinutes,
    startOfDay,
} from 'date-fns';
import React, { ReactNode, Ref, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

type DatePickerAction = 'clear' | 'today' | 'accept';

/** What the popup offers: a day calendar, a month grid, hour and minute columns, or the calendar beside the columns. */
type DatePickerMode = 'date' | 'month' | 'time' | 'datetime';

const DEFAULT_ACTIONS: DatePickerAction[] = ['clear', 'today', 'accept'];

const ACTION_LABELS: Record<DatePickerAction, string> = {
    clear: 'clear',
    today: 'time.today',
    accept: 'date-picker.accept',
};

/** The span the calendar's year dropdown offers unless a min or max date narrows it. */
const FIRST_MONTH = new Date(1900, 0, 1);
const LAST_MONTH = new Date(2099, 11, 1);

/** Any fixed day: it fills the parts a pattern leaves out, which do not decide whether an entry is complete. */
const REFERENCE_DAY = new Date(2000, 0, 1);

/** Stands in for an unreadable value where the field remembers what it last synced its text from. */
const UNREADABLE_VALUE = '\u0000';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const MONTHS = Array.from({ length: 12 }, (_, month) => month);

/** The popup element Base UI focuses on open: the calendar's current day, else the marked month or hour. */
const INITIAL_FOCUS = '[role=gridcell] button[tabindex="0"], [data-initial-focus]';

/**
 * The date a typed entry spells in `pattern`: `null` when blank, an invalid `Date` until it is complete. Only an
 * entry the pattern prints back exactly counts, since `yyyy-MM-d` would read "2026-09-2" as the 2nd and a year
 * still being typed as year 2.
 */
function readEntry(text: string, pattern: string, reference: Date): Date | null {
    const entry = text.trim();
    if (entry === '') {
        return null;
    }
    const parsed = parse(entry, pattern, reference);
    return isValid(parsed) && formatDate(parsed, pattern) === entry ? parsed : new Date(NaN);
}

/** Two picker values are the same when both are empty, both unreadable, or the same instant. */
function sameValue(a: Date | null, b: Date | null): boolean {
    if (a === null || b === null) {
        return a === b;
    }
    return isValid(a) ? a.getTime() === b.getTime() : !isValid(b);
}

/** The placeholder a field shows when its caller names none: the pattern, spelled the way it reads. */
function patternPlaceholder(pattern: string): string {
    return pattern.replace('yyyy', 'YYYY').replace('dd', 'DD').replace('HH', 'hh');
}

function scrollOptionToTop(option: HTMLButtonElement | null) {
    if (option?.parentElement) {
        option.parentElement.scrollTop = option.offsetTop;
    }
}

interface DatePickerFieldProps {
    value: Date | null;
    /** Receives the picked or typed value, `null` once cleared, and an invalid `Date` while a typed entry is incomplete. */
    onChange: (value: Date | null) => void;
    /** Whether the text reads as no date, on every edit of it: `onChange` is silent between two unreadable entries. */
    onUnreadableText?: (isUnreadable: boolean) => void;
    /** The date-fns pattern the field shows and reads. */
    format: string;
    mode?: DatePickerMode;
    /** The step of the minute column. */
    minuteStep?: number;
    minDate?: Date;
    maxDate?: Date;
    /** The buttons under the popup, in order. */
    actions?: DatePickerAction[];
    /** Shows a clear button in the field while it holds an entry. */
    clearable?: boolean;
    disabled?: boolean;
    /** Overrides the field's own check, which flags an unreadable entry or a date outside min/max. */
    invalid?: boolean;
    id?: string;
    placeholder?: string;
    inputRef?: Ref<HTMLInputElement>;
    /** Rendered at the end of the field, before its clear and picker buttons. */
    endAddon?: ReactNode;
    /** Classes for the bordered field, e.g. its width or height. */
    className?: string;
}

/**
 * A typed date, month, time or date-time field with a picker popup. The field shows and reads `format`, the caller
 * converts the `Date` to whatever it stores. A click anywhere in the field opens the popup and leaves the caret in
 * the text; the icon button opens it with focus on the current day, month or hour. Escape closes it and returns
 * focus to the text; tabbing out of the text closes it too.
 */
export function DatePickerField({
    value,
    onChange,
    onUnreadableText,
    format,
    mode = 'date',
    minuteStep = 5,
    minDate,
    maxDate,
    actions = DEFAULT_ACTIONS,
    clearable = true,
    disabled,
    invalid,
    id,
    placeholder,
    inputRef,
    endAddon,
    className,
}: DatePickerFieldProps) {
    const groupRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [openedFromText, setOpenedFromText] = useState(false);

    const current = value && isValid(value) ? value : null;
    const shown = current ? formatDate(current, format) : '';
    const valueKey = value && !current ? UNREADABLE_VALUE : shown;
    const [text, setText] = useState(shown);
    const [lastKey, setLastKey] = useState(valueKey);
    const [month, setMonth] = useState(() => current ?? new Date());
    const readable = isValid(readEntry(text, format, REFERENCE_DAY)) || text.trim() === '';
    if (valueKey !== lastKey) {
        setLastKey(valueKey);
        // The calendar turns to a new value's month, a typed one included; a day picked in view leaves it where it is.
        if (current && !isSameMonth(current, month)) {
            setMonth(current);
        }
        // A value set from outside replaces the text. An entry still being typed stays as it is while the caller
        // holds it as unreadable or empty; a clear that follows an unreadable value empties it.
        if (valueKey !== UNREADABLE_VALUE && (shown !== '' || readable || lastKey === UNREADABLE_VALUE)) {
            setText(shown);
        }
    }

    const outOfRange =
        current !== null &&
        ((minDate !== undefined && isBefore(current, startOfDay(minDate))) ||
            (maxDate !== undefined && isAfter(current, endOfDay(maxDate))));

    const textInput = () => groupRef.current?.querySelector('input') ?? null;

    const commit = (next: Date | null, close: boolean) => {
        if (sameValue(next, value)) {
            // Nothing to emit, so no new value comes back to replace a half-typed entry.
            setText(next ? formatDate(next, format) : '');
        } else {
            onChange(next);
        }
        if (close) {
            setOpen(false);
            textInput()?.focus({ preventScroll: true });
        }
    };

    // The time columns and a day picked beside them keep the rest of the value; an empty one starts at midnight today.
    const base = () => current ?? startOfDay(new Date());

    const pickDay = (day: Date) => {
        if (mode === 'datetime') {
            commit(setMinutes(setHours(day, base().getHours()), base().getMinutes()), false);
        } else {
            commit(day, true);
        }
    };

    const runAction = (action: DatePickerAction) => {
        if (action === 'clear') {
            commit(null, true);
        } else if (action === 'today') {
            commit(startOfDay(new Date()), true);
        } else {
            setOpen(false);
            textInput()?.focus({ preventScroll: true });
        }
    };

    const showCalendar = mode === 'date' || mode === 'datetime';
    const showClock = mode === 'time' || mode === 'datetime';
    const disabledDays = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])];

    return (
        <Popover
            open={open}
            onOpenChange={(next, details) => {
                // A press in the field itself (placing the caret, the clear button) leaves the popup open.
                if (
                    !next &&
                    details.reason === 'outside-press' &&
                    groupRef.current?.contains(details.event.target as Node)
                ) {
                    return;
                }
                if (next) {
                    setOpenedFromText(false);
                    setMonth(current ?? new Date());
                }
                setOpen(next);
            }}
        >
            <InputGroup ref={groupRef} className={cn('bg-paper', className)} data-disabled={disabled || undefined}>
                <InputGroupInput
                    ref={inputRef}
                    id={id}
                    value={text}
                    disabled={disabled}
                    placeholder={placeholder ?? patternPlaceholder(format)}
                    aria-invalid={invalid ?? (!readable || outOfRange)}
                    autoComplete="off"
                    data-form-type="other"
                    data-lpignore="true"
                    onClick={() => {
                        if (!open) {
                            setMonth(current ?? new Date());
                        }
                        setOpenedFromText(true);
                        setOpen(true);
                    }}
                    onKeyDown={(event) => {
                        // Focus stays in the text when the calendar opens from it, so Escape lands here rather than
                        // in the popup and would otherwise close the dialog the field sits in.
                        if (event.key === 'Escape' && open) {
                            event.stopPropagation();
                            setOpen(false);
                        }
                    }}
                    onBlur={(event) => {
                        // A complete entry typed without its padding ("3.9.2026" for dd.MM.yyyy) counts once the
                        // field is left; while typing it would read "2026-09-2" as the 2nd.
                        const loose = parse(text.trim(), format, base());
                        if (!readable && isValid(loose) && loose.getFullYear() >= 1000) {
                            commit(loose, false);
                        }
                        // Tabbing out of the text closes the popup. A press that focuses nothing leaves it to the
                        // popup's own outside-press handling.
                        const next = event.relatedTarget;
                        if (next && !groupRef.current?.contains(next) && !popupRef.current?.contains(next)) {
                            setOpen(false);
                        }
                    }}
                    onChange={(event) => {
                        setText(event.target.value);
                        const next = readEntry(event.target.value, format, base());
                        onUnreadableText?.(next !== null && !isValid(next));
                        if (!sameValue(next, value)) {
                            onChange(next);
                        }
                    }}
                />
                {/* On touch its 24px buttons' 44px areas meet across the wider gap. */}
                <InputGroupAddon align="inline-end" className="pointer-coarse:gap-5">
                    {endAddon}
                    {clearable && text !== '' && !disabled ? (
                        <InputGroupButton
                            size="icon-xs"
                            // Pointer-only, as the chevron of the kit combobox is: the text clears from the keyboard.
                            tabIndex={-1}
                            aria-label={t('clear')}
                            className="pointer-fine:opacity-0 pointer-fine:group-focus-within/input-group:opacity-100 pointer-fine:group-hover/input-group:opacity-100"
                            onClick={() => commit(null, false)}
                        >
                            <Clear fontSize="small" />
                        </InputGroupButton>
                    ) : null}
                    <PopoverTrigger
                        disabled={disabled}
                        aria-label={t(mode === 'time' ? 'date-picker.choose-time' : 'date-picker.choose-date')}
                        render={<InputGroupButton size="icon-xs" />}
                    >
                        {mode === 'time' ? <AccessTimeOutlined /> : <CalendarIcon />}
                    </PopoverTrigger>
                </InputGroupAddon>
            </InputGroup>
            <PopoverContent
                ref={popupRef}
                anchor={groupRef}
                align="start"
                positionerClassName="z-(--z-dropdown)"
                className="w-auto gap-0 p-0"
                initialFocus={() =>
                    !openedFromText && (popupRef.current?.querySelector<HTMLElement>(INITIAL_FOCUS) ?? true)
                }
                finalFocus={(closeType) => closeType === 'keyboard' && (textInput() ?? false)}
            >
                <div className="flex">
                    {showCalendar ? (
                        <Calendar
                            mode="single"
                            selected={current ?? undefined}
                            month={month}
                            onMonthChange={setMonth}
                            onSelect={(_selected, day) => pickDay(day)}
                            disabled={disabledDays}
                            startMonth={minDate ?? FIRST_MONTH}
                            endMonth={maxDate ?? LAST_MONTH}
                            weekStartsOn={1}
                            showOutsideDays={false}
                            captionLayout="dropdown"
                            formatters={{
                                formatMonthDropdown: (month) => formatDate(month, 'MMM'),
                                formatWeekdayName: (weekday) => formatDate(weekday, 'EEEEE'),
                            }}
                        />
                    ) : null}
                    {mode === 'month' ? <MonthGrid selected={current} onPick={(month) => commit(month, true)} /> : null}
                    {showClock ? (
                        // Beside the calendar the columns take its height, which a 4, 5 or 6 week month changes.
                        <div className={cn('flex', mode === 'datetime' ? 'border-border border-l' : 'h-60')}>
                            <TimeColumn
                                label={t('date-picker.hours')}
                                values={HOURS}
                                selected={current?.getHours()}
                                onPick={(hour) => commit(setHours(base(), hour), false)}
                            />
                            <TimeColumn
                                label={t('date-picker.minutes')}
                                values={Array.from({ length: 60 / minuteStep }, (_, index) => index * minuteStep)}
                                selected={current?.getMinutes()}
                                onPick={(minute) => commit(setMinutes(base(), minute), true)}
                            />
                        </div>
                    ) : null}
                </div>
                {actions.length > 0 ? (
                    <div className="border-border flex justify-end gap-2 border-t p-2">
                        {actions.map((action) => (
                            <Button
                                key={action}
                                variant="ghost"
                                size="sm"
                                className="text-primary"
                                onClick={() => runAction(action)}
                            >
                                {t(ACTION_LABELS[action])}
                            </Button>
                        ))}
                    </div>
                ) : null}
            </PopoverContent>
        </Popover>
    );
}

/**
 * Twelve months under a year stepper. A pick is the first of that month.
 *
 * ponytail: min/max are not applied here; the one month field has neither. Disable months against them when one does.
 */
function MonthGrid({ selected, onPick }: { selected: Date | null; onPick: (month: Date) => void }) {
    const [year, setYear] = useState(() => (selected ?? new Date()).getFullYear());

    return (
        <div className="flex w-64 flex-col gap-2 p-3">
            <div className="flex items-center justify-between">
                <span className="pl-1 text-sm font-medium">{year}</span>
                <div className="flex gap-1">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('date-picker.previous-year')}
                        onClick={() => setYear(year - 1)}
                    >
                        <ChevronLeft fontSize="small" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('date-picker.next-year')}
                        onClick={() => setYear(year + 1)}
                    >
                        <ChevronRight fontSize="small" />
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-1">
                {MONTHS.map((monthIndex) => {
                    const month = new Date(year, monthIndex, 1);
                    const isSelected = selected !== null && isSameMonth(selected, month);
                    return (
                        <Button
                            key={monthIndex}
                            variant={isSelected ? 'default' : 'ghost'}
                            size="sm"
                            className="rounded-full font-normal"
                            aria-pressed={isSelected}
                            data-initial-focus={isSelected || undefined}
                            onClick={() => onPick(month)}
                        >
                            {formatDate(month, 'MMM')}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
}

/** One column of the clock: a listbox the arrow keys walk, Home and End jump across, Enter or a click picks from. */
function TimeColumn({
    label,
    values,
    selected,
    onPick,
}: {
    label: string;
    values: number[];
    selected: number | undefined;
    onPick: (value: number) => void;
}) {
    const tabStop = selected !== undefined && values.includes(selected) ? selected : values[0];

    const moveFocus = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const options = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role=option]'));
        const index = options.indexOf(document.activeElement as HTMLElement);
        const target = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: options.length - 1 }[event.key];
        if (target === undefined) {
            return;
        }
        event.preventDefault();
        options[Math.min(Math.max(target, 0), options.length - 1)]?.focus();
    };

    return (
        <div
            role="listbox"
            aria-label={label}
            onKeyDown={moveFocus}
            // No height of its own: the column fills its row and scrolls, rather than stretching the popup to 24 hours.
            className="border-border relative flex h-0 min-h-full w-14 [scrollbar-width:none] flex-col gap-0.5 overflow-y-auto p-1 not-first:border-l"
        >
            {values.map((option) => (
                <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={option === selected}
                    tabIndex={option === tabStop ? 0 : -1}
                    data-initial-focus={option === tabStop || undefined}
                    ref={option === selected ? scrollOptionToTop : undefined}
                    onClick={() => onPick(option)}
                    className={cn(pickerOptionClassName, 'shrink-0 cursor-pointer')}
                >
                    {String(option).padStart(2, '0')}
                </button>
            ))}
        </div>
    );
}
